/**
 * Player display preferences — the ones that belong to the *device*, not the account.
 *
 * Kept out of `session` and out of `persist.ts`'s snapshot on purpose. That snapshot
 * is a cache of server state, scoped to a user uuid and expiring after a day; a
 * brightness setting is none of those things. It belongs to the screen the player is
 * holding, it should survive signing out, and it must never expire — a cave that
 * silently returns to its default darkness overnight is a bug report.
 *
 * Nothing here is authoritative over anything. These values change how the cave is
 * *painted* and touch no reward, no gate and no balance.
 */
import { create } from 'zustand';

import { BRIGHTNESS_DEFAULT, clampBrightness } from '@/game/engine/lighting';

const KEY = 'shwetap.display';

interface Stored {
  brightness?: number;
}

/** Every read is "the defaults" on failure — a blocked or corrupt store must not
 *  stop the game from painting. */
function read(): Stored {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Stored) : {};
  } catch {
    return {};
  }
}

function write(value: Stored): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(value));
  } catch {
    /* Private window, or storage full. The setting still holds for this session. */
  }
}

interface SettingsState {
  /** `BRIGHTNESS_MIN`–`BRIGHTNESS_MAX`. See `game/engine/lighting.ts`. */
  brightness: number;
  setBrightness(value: number): void;
  resetBrightness(): void;
}

export const useSettings = create<SettingsState>((set) => ({
  /* Clamped on the way in as well as on the way out: a hand-edited localStorage
     value of 0 would otherwise be a black screen with no way back to the slider. */
  brightness: clampBrightness(read().brightness),

  setBrightness(value) {
    const brightness = clampBrightness(value);
    write({ ...read(), brightness });
    set({ brightness });
  },

  resetBrightness() {
    write({ ...read(), brightness: BRIGHTNESS_DEFAULT });
    set({ brightness: BRIGHTNESS_DEFAULT });
  },
}));

export const selectBrightness = (s: SettingsState): number => s.brightness;
